// Theme Configuration
// This file defines both dark and light theme color palettes

export const darkTheme = {
  // Primary brand colors
  primary: '#6366F1',
  primaryLight: '#8B5CF6',
  primaryLighter: '#A78BFA',
  primaryDark: '#5558D9',
  
  // Purple colors (main theme)
  purple: '#6366F1',
  purpleLight: '#8B5CF6',
  purpleLighter: '#A78BFA',
  
  // Secondary colors
  secondary: '#93C5FD',
  secondaryLight: '#93C5FD',
  secondaryDark: '#3B82F6',
  
  // Accent colors
  accent: '#7A6FF0',
  accentLight: '#A78BFA',
  accentDark: '#6366F1',
  
  // Background colors (deep slate with subtle blue undertone)
  bgPrimary: '#0C0F19',
  bgSecondary: '#101420',
  bgHeader: '#0C0F19',
  bgCard: '#0E111A',
  bgInput: '#0E1118',
  bg: '#0E1118', // alias for backwards compatibility

  // Sidebar (elevated above bgPrimary, old app surface tones)
  sidebarBg: '#141928',
  sidebarBgFooter: '#121520',
  sidebarBorder: '#243044',
  
  // Border colors (blue-grey, refined glow on cards)
  border: '#1E2634',
  borderLight: '#2A3344',
  borderDark: '#0A0C14',
  borderResize: 'rgba(255, 255, 255, 0.24)',
  
  // Card elevation (used via colors.cardShadow in components)
  cardShadow: '0 1px 0 rgba(99, 102, 241, 0.07), 0 4px 16px rgba(0, 0, 0, 0.36)',
  
  // Input-specific colors
  inputPrimary: '#6366F1',
  inputSecondary: '#718096',
  inputActive: '#6366F1',
  inputDisabled: '#718096',
  
  // Text colors (slightly cooler for contrast on darker surfaces)
  text: '#E8EDF4',
  textLight: '#A8B4C8',
  textMuted: '#7B8A9E',
  textDark: '#2D3748',
  
  // Status colors
  success: '#07CBA3',
  warning: '#ED8936',
  danger: '#F73263',
  info: '#4299E1',
  
  // Chart colors
  chartUp: '#07CBA3',
  chartDown: '#F73263',
  chartNeutral: '#E2E8F0',
  chartBackground: '#0C0E16',
  chartGrid: '#1A2230',
  chartBorder: '#1E2634',
  crosshair: 'rgba(224, 227, 235, 0.1)',
  
  // RSI indicator colors
  rsiOverbought: '#F73263',
  rsiOversold: '#07CBA3',
  
  // Position colors
  positionLong: '#07CBA3',
  positionShort: '#F73263',
  
  // Alert colors
  alertAbove: '#FF4444',
  alertBelow: '#44FF44',
  
  // Basic colors
  white: '#FFFFFF',
  black: '#000000',
  transparent: 'transparent',
};

export const lightTheme = {
  // Primary brand colors (same as dark for consistency)
  primary: '#6366F1',
  primaryLight: '#8B5CF6',
  primaryLighter: '#A78BFA',
  primaryDark: '#5558D9',
  
  // Purple colors (main theme)
  purple: '#6366F1',
  purpleLight: '#8B5CF6',
  purpleLighter: '#A78BFA',
  
  // Secondary colors
  secondary: '#60A5FA',
  secondaryLight: '#93C5FD',
  secondaryDark: '#3B82F6',
  
  // Accent colors
  accent: '#7A6FF0',
  accentLight: '#A78BFA',
  accentDark: '#6366F1',
  
  // Background colors (slightly muted with a subtle cool tint to reduce glare)
  bgPrimary: '#F1F3F7',
  bgSecondary: '#E6EAF1',
  bgHeader: '#E8ECF4',
  bgCard: '#F8FAFD',
  bgInput: '#E6EAF1',
  bg: '#E6EAF1', // alias for backwards compatibility

  // Sidebar (match dark theme sidebar grays)
  sidebarBg: '#141928',
  sidebarBgFooter: '#121520',
  sidebarBorder: '#243044',
  
  // Border colors
  border: '#D6DCE6',
  borderLight: '#E2E7EF',
  borderDark: '#C1CAD6',
  borderResize: '#B0BAC8',
  
  cardShadow: '0 1px 2px rgba(15, 23, 42, 0.06), 0 1px 3px rgba(99, 102, 241, 0.04)',
  
  // Input-specific colors
  inputPrimary: '#6366F1',
  inputSecondary: '#718096',
  inputActive: '#6366F1',
  inputDisabled: '#CBD5E0',
  
  // Text colors
  text: '#1A202C',
  textLight: '#1F2937', // Darker grey for labels
  textMuted: '#374151', // Darker grey for secondary labels
  textDark: '#111827',
  
  // Status colors
  success: '#06B48A',
  warning: '#D97706',
  danger: '#DC2626',
  info: '#3B82F6',
  
  // Chart colors
  chartUp: '#06B48A',
  chartDown: '#DC2626',
  chartNeutral: '#4A5568',
  chartBackground: '#F9FAFB',
  chartGrid: '#E5E7EB',
  chartBorder: '#D1D5DB',
  crosshair: 'rgba(0, 0, 0, 0.1)',
  
  // RSI indicator colors
  rsiOverbought: '#DC2626',
  rsiOversold: '#06B48A',
  
  // Position colors
  positionLong: '#06B48A',
  positionShort: '#DC2626',
  
  // Alert colors
  alertAbove: '#DC2626',
  alertBelow: '#06B48A',
  
  // Basic colors
  white: '#FFFFFF',
  black: '#000000',
  transparent: 'transparent',
};

// Legacy colors for backward compatibility
export const legacyColors = {
  purple: '#6366F1',
  purpleLight: '#8B5CF6',
  purpleLighter: '#A78BFA',
  purpleAccent: '#7A6FF0',
  periwinkle: '#C5C5FF',
  periwinkleLight: '#DDDEFF',
  periwinkleLighter: '#EEEEFF',
  periwinkleAccent: '#B8B8FF',
  turquoise: '#40E0D0',
  turquoiseLight: '#7FEFDF',
  turquoiseLighter: '#B3F5EF',
  turquoiseAccent: '#20B2AA',
  gold: '#FFD700',
  orange: '#FF8C42',
  rose: '#FF4F70',
};

// Helper to get theme by name
export const getTheme = (themeName = 'dark') => {
  return themeName === 'light' ? lightTheme : darkTheme;
};

// Theme object for easy import (includes helpers)
export const createThemeObject = (colors) => ({
  colors,
  
  // Gradients
  gradients: {
    primary: `linear-gradient(45deg, ${colors.primary}, ${colors.secondary})`,
    secondary: `linear-gradient(45deg, ${colors.bgSecondary}, ${colors.borderLight})`,
    background: `linear-gradient(135deg, ${colors.bgPrimary} 0%, ${colors.bgSecondary} 50%, ${colors.bgHeader} 100%)`,
  },
  
  // Shadows
  shadows: {
    small: '0 1px 3px rgba(0, 0, 0, 0.12)',
    medium: '0 4px 6px rgba(0, 0, 0, 0.15)',
    large: '0 10px 15px rgba(0, 0, 0, 0.2)',
    card: colors.cardShadow,
  },
  
  // Border radius
  borderRadius: {
    small: '4px',
    medium: '8px',
    large: '12px',
    round: '50%',
  },
  
  // Spacing
  spacing: {
    xs: '0.25rem',
    sm: '0.5rem',
    md: '1rem',
    lg: '1.5rem',
    xl: '2rem',
    xxl: '3rem',
  },
  
  // Font sizes
  fontSize: {
    xs: '0.75rem',
    sm: '0.875rem',
    base: '1rem',
    lg: '1.125rem',
    xl: '1.25rem',
    xxl: '1.5rem',
  }
});

const themes = {
  dark: darkTheme,
  light: lightTheme,
  legacy: legacyColors,
  getTheme,
  createThemeObject,
};
export default themes;

